import { type FC } from "react";
import { Link } from "react-router-dom";

import { type OptionItem } from "@/api/admin";
import {
  ConsumptionStatus,
  cunsumptionStatusMap,
  FUNDING_CONSUMPTION_STATUS_MAP,
  type FundingConsumptionTotals,
  type IConsumption,
} from "@/api/admin/fundings";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import type { PaginatedResponse } from "@/types/api";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { formatUtcCalendarDate } from "@/utils/formatIsoToUTC";
import { formatNumber } from "@/utils/formatNumber";
import { useAdminMoney } from "@/utils/hooks/useAdminMoney";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

enum TableColumn {
  APPLICATION_ID = "Application ID",
  CREDIT_START_DATE = "Credit start date",
  ALLOCATED_AMOUNT = "Allocated amount",
  CONSUMED_CREDIT = "Consumed credit",
  REMAINING_CREDIT = "Remaining credit",
  STATUS = "Status",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TableColumn);

interface FundingTableProps {
  tableData?: PaginatedResponse<IConsumption> &
    Partial<FundingConsumptionTotals>;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
  isLinkToDetailsPage?: boolean;
}

const FundingConsumptionTable: FC<FundingTableProps> = ({
  tableData,
  tableParams,
  setTableParams,
  handleNext,
  handlePrev,
  searchValue,
  setSearchValue,
  isPending,
  isLinkToDetailsPage = true,
}) => {
  const money = useAdminMoney();
  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const { dateFrom: fromDate, dateTo: toDate } = parseDateRangeToParams(
      selectedMap[TableColumn.CREDIT_START_DATE]?.[0],
      true
    );

    const selectedStatuses =
      (selectedMap[TableColumn.STATUS] as string[] | undefined) ?? [];

    const rawStatuses = selectedStatuses
      .map(status => cunsumptionStatusMap[status])
      .filter(Boolean);

    setTableParams(prev => ({
      ...prev,
      fromDate,
      toDate,
      status: rawStatuses.length ? rawStatuses : undefined,
      offset: 0,
    }));
  };

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [TableColumn.STATUS]: Object.keys(cunsumptionStatusMap),
    [TableColumn.CREDIT_START_DATE]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [TableColumn.STATUS]: { type: "status" },
    [TableColumn.CREDIT_START_DATE]: { type: "date" },
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const hasFilters =
    searchValue ||
    (tableParams.status as string[])?.length ||
    tableParams.fromDate ||
    tableParams.toDate;

  const hasTotals =
    typeof tableData?.totalAmount === "number" &&
    typeof tableData.totalConsumedAmount === "number" &&
    typeof tableData.totalRemainingAmount === "number";

  const columns: ColumnConfig<IConsumption>[] = [
    {
      key: "referenceNumber",
      label: TableColumn.APPLICATION_ID,
      visible: flags[TableColumn.APPLICATION_ID],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        isLinkToDetailsPage ? (
          <TableCellLink last={isLast}>
            <Link
              to={PATHS.ADMIN.APPLICATION(row.id)}
              state={{ from: `${location.pathname}#funding:consumption` }}
              data-testid={`link-${row.id}`}
            >
              {row.referenceNumber}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.referenceNumber}</TableCellText>
        ),
      footerRenderer: hasTotals ? () => "Total" : undefined,
    },
    {
      key: "startDate",
      label: TableColumn.CREDIT_START_DATE,
      visible: flags[TableColumn.CREDIT_START_DATE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatUtcCalendarDate(row.startDate) || "-"}
        </TableCellText>
      ),
      footerRenderer: hasTotals ? () => null : undefined,
    },
    {
      key: "amount",
      label: TableColumn.ALLOCATED_AMOUNT,
      visible: flags[TableColumn.ALLOCATED_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatNumber(row?.amount, money.symbol("h-2.5"), money.decimals)}
        </TableCellText>
      ),
      footerRenderer: hasTotals
        ? () =>
            formatNumber(
              tableData.totalAmount ?? 0,
              money.symbol("h-2.5"),
              money.decimals
            )
        : undefined,
    },
    {
      key: "consumedAmount",
      label: TableColumn.CONSUMED_CREDIT,
      visible: flags[TableColumn.CONSUMED_CREDIT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.status === ConsumptionStatus.UPCOMING
            ? "-"
            : formatNumber(
                row?.consumedAmount,
                money.symbol("h-2.5"),
                money.decimals
              )}
        </TableCellText>
      ),
      footerRenderer: hasTotals
        ? () =>
            formatNumber(
              tableData.totalConsumedAmount ?? 0,
              money.symbol("h-2.5"),
              money.decimals
            )
        : undefined,
    },
    {
      key: "remainingAmount",
      label: TableColumn.REMAINING_CREDIT,
      visible: flags[TableColumn.REMAINING_CREDIT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.status === ConsumptionStatus.UPCOMING
            ? "-"
            : formatNumber(
                row?.remainingAmount,
                money.symbol("h-2.5"),
                money.decimals
              )}
        </TableCellText>
      ),
      footerRenderer: hasTotals
        ? () =>
            formatNumber(
              tableData.totalRemainingAmount ?? 0,
              money.symbol("h-2.5"),
              money.decimals
            )
        : undefined,
    },
    {
      key: "status",
      label: TableColumn.STATUS,
      visible: flags[TableColumn.STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row.status ? (
          <TableCellBadge
            last={isLast}
            stage={
              FUNDING_CONSUMPTION_STATUS_MAP[formatEnumValue(row?.status)]
                ?.badge
            }
          >
            {formatEnumValue(row?.status) ?? "-"}
          </TableCellBadge>
        ) : (
          <TableCellText last={isLast}>-</TableCellText>
        ),
      footerRenderer: hasTotals ? () => null : undefined,
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={FUNDING_CONSUMPTION_STATUS_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isPending ? (
        <Loader />
      ) : (
        <DynamicTable<IConsumption>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
          notFoundTitle={
            hasFilters ? "No records match your filters." : "No records found"
          }
        />
      )}
    </>
  );
};

export default FundingConsumptionTable;
